// stores/walletStore.js
import { defineStore } from 'pinia';
import { fetchAllWalletData } from '@/composables/multiService';
import { useWalletAccount, connectNativeWallet, disconnectNativeWallet, restoreNativeWallet } from '@/composables/useWalletAccount';
const accountData = useWalletAccount();
const requests = new WeakMap();
const recoveries = new WeakMap();

export const useWalletStore = defineStore('walletStore', {
  state: () => ({
    dataAddress: '',
    dataStatus: { contracts: 'idle', polBalance: 'idle', pctPrice: 'idle' },
    dataErrors: {},
    connectionError: '',
    isCheckState: false,
    pctBalance: null,
    pineCount: null,
    usdtBalance: null,
    polBalance: null,
    pcdBalance: null,
    xpctBalance: null,
    pctPrice: null,
    polPrice: null,
    pcdPrice: null,
    availableBalance: null,
    accountType: null,
    balanceOfShares: null,
    balanceOfAvailable: null,
    totalSharesForDividends: null,
    lastFetchTime: 0,
    pinePrice:0,
    referralIncomes: null,
    userDonateCount: null,
    selfReferralCount: null,
    selfReferralDonateCount: null,
    miningPoolCount: null,
    miningPerBlock: null,
    miningSpeed: null,
    totalMiningOutput: null,
    remainingMiningOutput: null,
    directorUpgradeCount: null,
    pctAllowanceToXpct: null, // PCT授權給XPCT合約的數量
    totalStaked: null, // 新增 質押PCT總量
    directorOnly: null, // 是否僅董事可質押
    dividendOf: null, // PCD合約待領取金額
    getClaimedPCD: 0, //可以領取的PCD數量
    minPineForClaim: 0,
    getClaimablePCD: 0, //這是可以領取的數量
    accumulativeDividendOf: 0, //累計分紅 
  }),
  getters: {
    currentAccount: () => accountData.value?.address || '',
    isConnected: () => !!accountData.value?.address,
    hasContractData: state => state.pcdBalance !== null,
  },
  actions: {
    resetAccountData() {
      requests.set(this, Symbol('reset'));
      this.$reset();
    },
    async connectWallet() {
      this.connectionError = '';
      this.isCheckState = true;
      try {
        await connectNativeWallet();
        await this.fetchAllData(true);
      } catch (error) {
        this.connectionError = error.code === 4001 ? '已取消連接或切換網路。' : (error.shortMessage || error.message || '連接錢包失敗');
      } finally {
        this.isCheckState = false;
      }
    },
    async disconnectWallet() {
      // Invalidate first: a late RPC response must not resurrect disconnected balances.
      this.resetAccountData();
      try { await disconnectNativeWallet(); }
      catch (error) { this.connectionError = error.message; }
    },
    smartConnectWallet() {
      if (recoveries.has(this)) return recoveries.get(this);
      const previousAddress = accountData.value?.address?.toLowerCase();
      const operation = (async () => {
        try {
          await restoreNativeWallet();
          if (accountData.value?.address) {
            this.connectionError = '';
            // A same-account foreground check must not invalidate pending transaction guards.
            if (accountData.value.address.toLowerCase() !== previousAddress || !this.hasContractData)
              await this.fetchAllData(true);
          }
        } catch (error) { this.connectionError = error.message; }
      })();
      const tracked = operation.finally(() => {
        if (recoveries.get(this) === tracked) recoveries.delete(this);
      });
      recoveries.set(this, tracked);
      return tracked;
    },
    async fetchAllData(force = false) {
      const address = accountData.value?.address;
      if (!address) { this.resetAccountData(); return; }
      const key = address.toLowerCase();
      if (this.dataAddress !== key) {
        this.resetAccountData();
        this.dataAddress = key;
      }
      if (!force && this.lastFetchTime && Date.now() - this.lastFetchTime < 5 * 60 * 1000
        && Object.values(this.dataStatus).every(value => value === 'ready')) return;
      const request = Symbol(key);
      requests.set(this, request);
      const isCurrent = () => requests.get(this) === request
        && accountData.value?.address?.toLowerCase() === key;
      const { getEthBalance } = await import('@/composables/ethService');
      const { getPCTRealPrice } = await import('@/composables/contractPctService');
      const load = async (group, fetch, apply) => {
        if (!isCurrent()) return;
        this.dataStatus[group] = 'loading';
        delete this.dataErrors[group];
        try {
          const result = await fetch();
          if (!isCurrent()) return;
          if (result === null || result === undefined) throw new Error('資料來源未回傳有效結果');
          apply(result);
          this.dataStatus[group] = 'ready';
        } catch (error) {
          if (!isCurrent()) return;
          this.dataStatus[group] = 'error';
          this.dataErrors[group] = error.shortMessage || error.message || '讀取失敗';
        }
      };
      await Promise.all([
        load('contracts', () => fetchAllWalletData(address), data => {
          Object.keys(data).forEach(key => { if (key in this) this[key] = data[key]; });
        }),
        load('polBalance', () => getEthBalance(address), value => { this.polBalance = value; }),
        load('pctPrice', getPCTRealPrice, value => { this.pctPrice = value; }),
      ]);
      if (isCurrent()) this.lastFetchTime = Date.now();
    },
  }
});
